// Screen 2 (spec 13 §Screens; Reports / Ops UX pass 2026-10-21, Vercel and Linear consoles): the
// operator's home. The 56 px location header (Companies, count, Gate tracker, New company), a
// 40 px toolbar (week, search, the failed-events chip), four compact tiles, and one table row
// per granted company from `GET /v1/operator/companies?week=` (a per-company fan-out on the
// server; the app never joins across companies): band, KPI, cost, supervisor and founder hours,
// the week's report, last ingest, waiting approvals (summed from the DE list) and failed events
// (from the consumer records). Three API calls on load. Hover (always on coarse pointers): Open
// company, Grant / revoke add-on (the add-ons drawer, coverage audit rows 82-83), AiGentPanel
// (a one-time launch link, ADI-02). A company without `kpi` shows "KPI off" instead of band and
// score, and no report link (ADR-0016).
import {
  Badge,
  BandBadge,
  CompanyTile,
  EmptyState,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  SearchField,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Toolbar,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowUpRightIcon,
  Building2Icon,
  FlagIcon,
  HourglassIcon,
  PackageIcon,
  TimerIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { MeResponse, OperatorCompanyRow } from "../../api/types";
import { recentWeeks } from "../../focus/focus";
import { weekRangeLabel } from "../../focus/weeks";
import { balanceText } from "../billing/billing-money";
import { type WalletStatus, WalletStatusChip } from "../billing/billing-parts";
import { utcMonth, walletMarginQuery } from "../billing/billing-queries";
import {
  formatDateTime,
  formatHours,
  formatMoney,
  formatRecent,
  formatScore,
  REPORT_STATE_LABELS,
} from "../format";
import { operatorDesQuery } from "../ops-queries";
import {
  LinkButton,
  OpsBody,
  OpsFrame,
  RowAction,
  RowActions,
  RowLinkAction,
  TABLE_ROW_HOVER,
  Tiles,
} from "../ops-ux";
import { operatorCompaniesQuery, operatorConsumersQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { featureState } from "../use-feature";
import { AddonsDrawer } from "./addons-drawer";
import { OperatorAgentRequests } from "./agent-requests-card";
import { NewCompanyDialog } from "./new-company-dialog";
import { OpenAigentpanelIconButton } from "./open-aigentpanel";

export function OperatorConsole({
  api,
  me,
  week,
  onWeek,
}: {
  api: Api;
  me: MeResponse;
  week: string;
  onWeek: (week: string) => void;
}) {
  const companies = useQuery(operatorCompaniesQuery(api, week));
  const consumers = useQuery(operatorConsumersQuery(api));
  const des = useQuery(operatorDesQuery(api, week));
  // WALLET-P1 §6.3: each company's credit status, one read for the whole console.
  const wallets = useQuery(walletMarginQuery(api, utcMonth(new Date())));
  const walletBy = new Map(
    (wallets.data?.companies ?? []).map((c) => [
      c.company.id,
      { status: c.status, balance: c.balance_micros },
    ]),
  );
  const [addons, setAddons] = useState<{ id: string; name: string } | null>(null);
  const [search, setSearch] = useState("");
  const weeks = useMemo(() => {
    const list = recentWeeks(new Date(), 12);
    return list.includes(week) ? list : [week, ...list];
  }, [week]);
  const failedBy = new Map<string, number>();
  for (const c of consumers.data?.data ?? [])
    if (c.state === "failed") failedBy.set(c.company_id, (failedBy.get(c.company_id) ?? 0) + 1);
  const waitingBy = new Map<string, number>();
  for (const d of des.data?.data ?? [])
    waitingBy.set(d.company.id, (waitingBy.get(d.company.id) ?? 0) + d.pending_approvals);
  const failed = [...failedBy.values()].reduce((a, b) => a + b, 0);
  const waiting = [...waitingBy.values()].reduce((a, b) => a + b, 0);
  const rows = companies.data?.data ?? [];
  const needle = search.trim().toLowerCase();
  const shown = needle ? rows.filter((r) => r.company.name.toLowerCase().includes(needle)) : rows;
  const deCount = rows.reduce((a, r) => a + r.de_count, 0);
  const supervisor = rows.reduce((a, r) => a + r.supervisor_minutes, 0);

  return (
    <OpsFrame>
      <LocationHeader
        icon={Building2Icon}
        title="Companies"
        count={companies.isPending ? undefined : rows.length}
        actions={
          <>
            <LinkButton render={<Link to="/operator/gate" />} className="max-md:hidden">
              <FlagIcon aria-hidden="true" />
              Gate tracker
            </LinkButton>
            <NewCompanyDialog api={api} size="sm" />
          </>
        }
      />
      <Toolbar bleed>
        <NativeSelect
          size="sm"
          aria-label="Week"
          value={week}
          onChange={(e) => onWeek(e.target.value)}
          className="w-auto"
        >
          {weeks.map((w) => (
            <NativeSelectOption key={w} value={w}>
              {w} · {weekRangeLabel(w)}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <SearchField
          placeholder="Search companies…"
          aria-label="Search companies"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {failed > 0 ? (
          <span
            data-slot="failed-consumers"
            className="ml-auto flex shrink-0 items-center gap-1.5 text-xs text-warning-text"
          >
            <TriangleAlertIcon aria-hidden="true" className="size-3.5" />
            <span className="max-md:hidden">
              {failed} failed consumer {failed === 1 ? "record needs" : "records need"} an operator.
            </span>
            <span className="md:hidden">{failed} failed</span>
            <LinkButton size="xs" render={<Link to="/operator/consumers" />}>
              Review
            </LinkButton>
          </span>
        ) : null}
      </Toolbar>
      <OpsBody>
        <Tiles aria-label="This week across companies">
          <StatTile
            size="sm"
            label="Companies"
            icon={Building2Icon}
            value={companies.isPending ? "…" : rows.length}
            detail={`${deCount} ${deCount === 1 ? "digital employee" : "digital employees"}`}
          />
          <StatTile
            size="sm"
            label="Waiting approvals"
            icon={HourglassIcon}
            value={des.isPending ? "…" : waiting}
            detail="across every DE, now"
          />
          <StatTile
            size="sm"
            label="Failed events"
            icon={TriangleAlertIcon}
            value={consumers.isPending ? "…" : failed}
            detail={failed ? "need an operator" : "every consumer keeps up"}
          />
          <StatTile
            size="sm"
            label="Supervisor hours"
            icon={TimerIcon}
            value={companies.isPending ? "…" : formatHours(supervisor)}
            detail={`week ${week}`}
          />
        </Tiles>
        {/* MANAGED-POLICY: open requests for agents from managed companies (one side read). */}
        <OperatorAgentRequests api={api} />
        {companies.isPending ? (
          <Loading label="Loading companies" />
        ) : companies.isError ? (
          <LoadError what="Companies" error={companies.error} />
        ) : rows.length === 0 ? (
          <EmptyState
            variant="card"
            icon={Building2Icon}
            title="No companies yet"
            description="Create the first client company: it gets your agency's access and an owner invitation."
          />
        ) : (
          <CompaniesTable
            api={api}
            rows={shown}
            me={me}
            week={week}
            waitingBy={waitingBy}
            failedBy={failedBy}
            walletBy={walletBy}
            onAddons={(row) => setAddons({ id: row.company.id, name: row.company.name })}
          />
        )}
      </OpsBody>
      <AddonsDrawer api={api} me={me} company={addons} onClose={() => setAddons(null)} />
    </OpsFrame>
  );
}

function CompaniesTable({
  api,
  rows,
  me,
  week,
  waitingBy,
  failedBy,
  walletBy,
  onAddons,
}: {
  api: Api;
  rows: OperatorCompanyRow[];
  me: MeResponse;
  week: string;
  waitingBy: Map<string, number>;
  failedBy: Map<string, number>;
  walletBy: Map<string, { status: WalletStatus; balance: number }>;
  onAddons: (row: OperatorCompanyRow) => void;
}) {
  return (
    <Table data-slot="operator-companies">
      <TableCaption>
        Week {week} ({weekRangeLabel(week)}). KPI is the band and the score; cost includes agency
        rows; hours are the supervisors' logged time entries.
      </TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">Company</TableHead>
          <TableHead scope="col" className="text-right">
            DEs
          </TableHead>
          <TableHead scope="col" title="Band and score">
            KPI
          </TableHead>
          <TableHead scope="col" className="text-right" title="Cost of the week">
            Cost
          </TableHead>
          <TableHead scope="col" className="text-right" title="Supervisor hours of the week">
            Hours
          </TableHead>
          <TableHead scope="col" className="max-2xl:hidden">
            Report
          </TableHead>
          <TableHead scope="col">Last ingest</TableHead>
          <TableHead scope="col" className="text-right" title="Approvals waiting now">
            Waiting
          </TableHead>
          <TableHead scope="col" className="text-right" title="Failed consumer records">
            Failed
          </TableHead>
          <TableHead scope="col">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const kpi = featureState(me, row.company.id, "kpi");
          const waiting = waitingBy.get(row.company.id) ?? 0;
          const failed = failedBy.get(row.company.id) ?? 0;
          return (
            <TableRow
              key={row.company.id}
              data-company={row.company.slug}
              className={TABLE_ROW_HOVER}
            >
              <TableHead scope="row" className="font-normal">
                <span className="inline-flex items-center gap-2">
                  <CompanyTile name={row.company.name} size={20} />
                  <Link
                    to="/c/$slug"
                    params={{ slug: row.company.slug }}
                    search={{ p: week }}
                    className="font-medium underline-offset-4 hover:underline"
                  >
                    {row.company.name}
                  </Link>
                </span>
              </TableHead>
              <TableCell className="text-right tabular-nums">
                <Link
                  to="/c/$slug/agents"
                  params={{ slug: row.company.slug }}
                  search={{ p: week }}
                  aria-label={`${row.de_count} digital employees of ${row.company.name}`}
                  className="underline-offset-4 hover:underline"
                >
                  {row.de_count}
                </Link>
              </TableCell>
              <TableCell>
                {kpi.enabled ? (
                  <span className="inline-flex items-center gap-2">
                    {row.band ? <BandBadge band={row.band} /> : null}
                    <span className="text-muted-foreground tabular-nums">
                      {formatScore(row.kpi_score)}
                    </span>
                  </span>
                ) : (
                  <Badge variant="outline">KPI off</Badge>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatMoney(row.cost_minor, row.company.reporting_currency)}
                {(() => {
                  const w = walletBy.get(row.company.id);
                  return w ? (
                    <span
                      className="ml-2 inline-flex align-middle"
                      title={w.status === "off" ? "Credit off" : `Credit ${balanceText(w.balance)}`}
                    >
                      <WalletStatusChip status={w.status} />
                    </span>
                  ) : null;
                })()}
              </TableCell>
              <TableCell
                className="text-right tabular-nums"
                title={`Founder ${formatHours(row.founder_minutes)}`}
              >
                {formatHours(row.supervisor_minutes)}
              </TableCell>
              <TableCell className="max-2xl:hidden">
                {kpi.enabled && row.report_state ? (
                  <Link
                    to="/c/$slug/kpis/reports/$week"
                    params={{ slug: row.company.slug, week }}
                    className="underline-offset-4 hover:underline"
                  >
                    {/* The row header names the company; the link names the report. */}
                    <span className="sr-only">Report {week}: </span>
                    <Badge variant={row.report_state === "sent" ? "success" : "neutral"}>
                      {REPORT_STATE_LABELS[row.report_state]}
                    </Badge>
                  </Link>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell
                className="whitespace-nowrap text-muted-foreground tabular-nums"
                title={row.last_ingest_at ? formatDateTime(row.last_ingest_at) : undefined}
              >
                {row.last_ingest_at ? formatRecent(row.last_ingest_at) : "Never"}
              </TableCell>
              <TableCell className="text-right tabular-nums" data-slot="waiting">
                {waiting > 0 ? (
                  <Badge variant="info">{waiting}</Badge>
                ) : (
                  <span className="text-muted-foreground">0</span>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums" data-slot="failed">
                {failed > 0 ? (
                  <Link to="/operator/consumers" className="underline-offset-4 hover:underline">
                    <Badge variant="danger">{failed}</Badge>
                  </Link>
                ) : (
                  <span className="text-muted-foreground">0</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <RowActions>
                  <RowLinkAction
                    icon={ArrowUpRightIcon}
                    label={`Open ${row.company.name}`}
                    render={
                      <Link
                        to="/c/$slug"
                        params={{ slug: row.company.slug }}
                        search={{ p: week }}
                      />
                    }
                  />
                  <RowAction
                    icon={PackageIcon}
                    label={`Add-ons of ${row.company.name}`}
                    tooltip="Grant / revoke add-on"
                    onClick={() => onAddons(row)}
                  />
                  <OpenAigentpanelIconButton
                    api={api}
                    companyId={row.company.id}
                    companyName={row.company.name}
                  />
                </RowActions>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
